import fields from '@/field/table/voucher'
import { get, set } from '@/helper/localStorage'
import _get from 'lodash/get'

export default {
  namespaced: true,
  state: {
    tableFields: get('voucherV2TableFields', fields),
    tableData: [],
    tablePagination: get('voucherV2TablePagination'),
    tableKeyword: get('voucherV2TableKeyword'),
    tableSort: get('voucherV2TableSort'),
    tableFilter: get('voucherV2TableFilter', []),
    detailData: get('voucherV2DetailData'),
    loadingTable: false,
    loadingDetail: false,
    loadingSubmit: false,
  },
  getters: {
    tableFields: state => {
      if (!state.tableFields) {
        return undefined
      }

      // only the visibility is customizable by the user
      const visibilities = state.tableFields.reduce(
        (obj, item) => Object.assign(obj, { [item.key]: item.visibility }), {},
      )

      // the other props uses the default fields configuration
      return fields.map(item => ({
        ...item,
        // only replace the visibility value if user override it
        visibility: item.key in visibilities ? visibilities[item.key] : item.visibility,
      }))
    },
    tableSort: state => {
      if (!state.tableSort) {
        return undefined
      }

      const symbol = state.tableSort.direction === 'desc' ? '-' : ''

      return `${symbol}${state.tableSort.key}`
    },
    tableData: state => {
      if (!state.tableData) {
        return []
      }

      return state.tableData.map(item => ({
        ...item,
        // add or replace field here
      }))
    },
    detailData: state => {
      if (!state.detailData) {
        return undefined
      }

      return {
        ...state.detailData,
        // add or replace field here
      }
    },
    tableFilter: state => {
      if (!state.tableFilter) {
        return []
      }

      return state.tableFilter
        .reduce((acc, elem) => Object.assign(acc, {
          [`filter[${elem.key}]`]: Array.isArray(elem.value)
            ? elem.value.join(',')
            : elem.value,
        }), {})
    },
    tableParams: (state, getters, rootState) => ({
      'filter[keyword]': state.tableKeyword,
      ...getters.tableFilter,
      sort: getters.tableSort,
      page: _get(state, 'tablePagination.current_page', 1),
      limit: rootState.appConfig.settings.tablePerPage,
    }),
  },
  mutations: {
    SET_TABLE_FIELDS(state, val) {
      set('voucherV2TableFields', val)
      state.tableFields = val
    },
    RESET_TABLE_FIELDS(state) {
      set('voucherV2TableFields', fields)
      state.tableFields = fields
    },
    SET_TABLE_DATA(state, val) {
      state.tableData = val
    },
    SET_TABLE_PAGINATION(state, val) {
      set('voucherV2TablePagination', val)
      state.tablePagination = val
    },
    SET_TABLE_KEYWORD(state, val) {
      set('voucherV2TableKeyword', val)
      state.tableKeyword = val
    },
    SET_DETAIL_DATA(state, val) {
      set('voucherV2DetailData', val)
      state.detailData = val
    },
    SET_TABLE_SORT(state, val) {
      set('voucherV2TableSort', val)
      state.tableSort = val
    },
    SET_TABLE_FILTER(state, val) {
      set('voucherV2TableFilter', val)
      state.tableFilter = val
    },
    SET_LOADING_TABLE(state, val) {
      state.loadingTable = val
    },
    SET_LOADING_DETAIL(state, val) {
      state.loadingDetail = val
    },
    SET_LOADING_SUBMIT(state, val) {
      state.loadingSubmit = val
    },
  },
  actions: {
    getTableData({ commit, getters }) {
      commit('SET_LOADING_TABLE', true)

      const { tableParams: params } = getters
      this._vm.$http.get('/v2/admin/vouchers', { params })
        .then(({ data }) => {
          commit('SET_TABLE_DATA', data.data)
          commit('SET_TABLE_PAGINATION', data.meta)
        })
        .finally(() => {
          commit('SET_LOADING_TABLE', false)
        })
    },
    search(_, payload) {
      return this._vm.$http.get('/v2/admin/vouchers', {
        params: {
          'filter[ids]': payload ? payload.ids : undefined,
          'filter[keyword]': payload ? payload.keyword : undefined,
        },
      })
        .then(({ data }) => data.data)
        .catch(() => [])
    },
    getDetail({ commit }, id) {
      commit('SET_LOADING_DETAIL', true)

      this._vm.$http.get(`/v2/admin/vouchers/${id}`)
        .then(({ data }) => {
          commit('SET_DETAIL_DATA', data.data)
        })
        .finally(() => {
          commit('SET_LOADING_DETAIL', false)
        })
    },
    create({ commit, rootState }, data) {
      commit('SET_LOADING_SUBMIT', true)

      const normalizedData = rootState.auth.userData?.role === 'admin'
        ? data
        : {
          ...data,
          type: 'regular',
        }

      return this._vm.$http.post('/v2/admin/vouchers', normalizedData)
        .finally(() => {
          commit('SET_LOADING_SUBMIT', false)
        })
    },
    update({ commit, rootState, state }, { id, data }) {
      commit('SET_LOADING_SUBMIT', true)

      const normalizedData = rootState.auth.userData?.role === 'admin'
        ? data
        : {
          ...data,
          type: state.detailData?.type || 'regular',
        }

      return this._vm.$http.patch(`/v2/admin/vouchers/${id}`, normalizedData)
        .then(res => {
          commit('SET_DETAIL_DATA', res.data.data)
        })
        .finally(() => {
          commit('SET_LOADING_SUBMIT', false)
        })
    },
    delete({ commit }, id) {
      commit('SET_LOADING_SUBMIT', true)

      return this._vm.$http.delete(`/v2/admin/vouchers/${id}`)
        .finally(() => {
          commit('SET_LOADING_SUBMIT', false)
        })
    },
    searchRootCategories(_, payload) {
      return this._vm.$http.get('/v2/admin/categories', {
        params: {
          'filter[is_root]': true,
          'filter[keyword]': payload ? payload.keyword : undefined,
          'filter[ids]': payload ? payload.ids : undefined,
          limit: payload ? payload.perPage : undefined,
        },
      })
        .then(({ data }) => data.data)
        .catch(() => [])
    },
    searchMerchants(_, payload) {
      return this._vm.$http.get('/v3/admin/merchants', {
        params: {
          'filter[keyword]': payload ? payload.keyword : undefined,
          'filter[ids]': payload ? payload.ids : undefined,
          limit: payload ? payload.perPage : undefined,
        },
      })
        .then(({ data }) => data.data)
        .catch(() => [])
    },
  },
}
